import React, { useEffect } from "react";
import "./App.scss";
import { Route, Routes, Navigate } from "react-router-dom";
import AuthPage from "./pages/AuthPage/AuthPage";
import OrderProcessingPage from "./pages/OrderProcessingPage/OrderProcessingPage";
import PharmacistSupportPage from "./pages/PharmacistSupportPage/PharmacistSupportPage";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./store";
import { loginCheckState } from "./store/auth-actions";
import {
  selectIsAuthenticated,
  selectIsAdmin,
  selectIsMarketplaceAdmin,
  selectCanViewAdminMarketplace,
  selectCanViewTransactions,
  selectIsConsultOnlySession,
  selectHasSupportedPharmacistCapability,
  selectPersonaKey,
  selectTokenUserId,
} from "./store/auth-slice";
import { useDefaultRoute } from "./hooks/useDefaultRoute";
import ArticlesOutletPage from "./pages/ArticlesOutletPage/ArticlesOutletPage";
import CommercialsPage from "./pages/CommercialsPage/CommercialsPage";
import Layout from "./components/Layout/Layout";
import ArticlesVisibilityPage from "./pages/ArticlesVisibilityPage/ArticlesVisibilityPage";
import OurPatientsPage from "./pages/OurPatientsPage/OurPatientsPage";
import PushNotificationsPage from "./pages/PushNotificationsPage/PushNotificationsPage";
import Statistics from "./pages/Statistics/Statistics";
import UnifiedReminderPage from "./pages/UnifiedReminderPage/UnifiedReminderPage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import VideoConsultManager from "./components/VideoConsultManager/VideoConsultManager";
import ConsultOrderManager from "./components/ConsultOrder/ConsultOrderManager";
import SuspendedOrdersNotification from "./components/UI/SuspendedOrdersNotification/SuspendedOrdersNotification";
import TabNotificationManager from "./components/TabNotificationManager";
import AdminMarketPlacePage from "./pages/AdminMarketPlacePage/AdminMarketPlacePage";
import TransactionsPage from "./pages/TransactionsPage/Transactions";
import PharmacistProfilePage from "./pages/PharmacistProfilePage/PharmacistProfilePage";
import ConsultPharmacyContextLoader from "./components/ConsultPharmacyContextLoader";
// Loading component for authentication initialization
const AuthLoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      fontSize: "18px",
      fontFamily: "Arial, sans-serif",
      color: "#333",
    }}
  >
    Inicijalizacija autentifikacije...
  </div>
);

function App() {
  const dispatch = useDispatch();

  const canViewTransactions = useSelector(selectCanViewTransactions);

  // Authentication state
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isMarketplaceAdmin = useSelector(selectIsMarketplaceAdmin);
  const isAdmin = useSelector(selectIsAdmin);
  const canViewAdminMarketplace = useSelector(selectCanViewAdminMarketplace);
  const authInitialized = useSelector(
    (state: RootState) => state.auth.authInitialized,
  );
  // ITAM-2622
  const isConsultOnly = useSelector(selectIsConsultOnlySession);
  const hasPharmacistCapability = useSelector(
    selectHasSupportedPharmacistCapability,
  );
  const personaKey = useSelector(selectPersonaKey);
  const tokenUserId = useSelector(selectTokenUserId);
  const defaultRoute = useDefaultRoute();

  // Initialize authentication on app start
  useEffect(() => {
    console.log("App: Initializing authentication system");
    dispatch<any>(loginCheckState());
  }, [dispatch]);

  // Show loading spinner during authentication initialization
  if (!authInitialized) {
    return <AuthLoadingSpinner />;
  }

  // Determine default route for authenticated users (shared helper — internal
  // redirects use the same one, so no page can send a consult-only pharmacist
  // to a route they may not reach).
  const getDefaultRoute = () => defaultRoute;

  // Conditional route rendering based on authentication state
  return (
    <>
      {isAuthenticated && <VideoConsultManager />}
      {/* ITAM-2622 (3.6): the key is a SCALAR — `${id}:${personaObject}` would
          stringify to "42:[object Object]" and never change, silently defeating
          the teardown. A persona-changing token refresh must REMOUNT this
          subtree: `clearDraft()` alone would leave the lifecycle hook holding
          the phase, its own copy of the prefill (patient name, phone, address),
          the RFZO result, the timers and the in-flight aborts under a persona
          that must not see them. A same-persona refresh leaves the key
          unchanged, so a live consult flow is never disturbed. */}
      {isAuthenticated && (
        <ConsultOrderManager
          key={`${tokenUserId ?? "anon"}:${personaKey}`}
        />
      )}
      {isAuthenticated && <ConsultPharmacyContextLoader />}
      {isAuthenticated && <SuspendedOrdersNotification />}
      {isAuthenticated && <TabNotificationManager />}

      <Layout>
        <Routes>
          {!isAuthenticated ? (
            // ==========================================
            // UNAUTHENTICATED ROUTES
            // ==========================================
            <>
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/*" element={<AuthPage />} />
              {/* Redirect all other routes to auth */}
              <Route path="*" element={<Navigate to="/auth" replace />} />
            </>
          ) : (
            // ==========================================
            // AUTHENTICATED ROUTES
            // ==========================================
            <>
              <Route
                path="/auth"
                element={<Navigate to={getDefaultRoute()} replace />}
              />
              <Route
                path="/auth/*"
                element={<Navigate to={getDefaultRoute()} replace />}
              />
              <Route path="/profile" element={<ProfilePage />} />

              {isConsultOnly ? (
                // ITAM-2622 (decision 8): the consult-only allowlist is exactly
                // personal profile, adviser profile, consultation history.
                // Consultation CnC/RFZO live INSIDE the active-consult flow,
                // not as menu entries, and no retail/CMS route is exposed.
                <>
                  <Route
                    path="/pharmacist-profile"
                    element={<PharmacistProfilePage />}
                  />
                  <Route
                    path="/transactions"
                    element={
                      canViewTransactions ? (
                        <TransactionsPage />
                      ) : (
                        <Navigate to="/pharmacist-profile" replace />
                      )
                    }
                  />
                  <Route
                    path="*"
                    element={<Navigate to="/pharmacist-profile" replace />}
                  />
                </>
              ) : isMarketplaceAdmin && !isAdmin ? (
                <>
                  <Route
                    path="/admin-marketplace"
                    element={
                      canViewAdminMarketplace ? (
                        <AdminMarketPlacePage />
                      ) : (
                        <Navigate to={getDefaultRoute()} replace />
                      )
                    }
                  />
                  <Route
                    path="*"
                    element={<Navigate to="/admin-marketplace" replace />}
                  />
                </>
              ) : (
                <>
                  <Route
                    path="/order-processing"
                    element={<OrderProcessingPage />}
                  />
                  <Route
                    path="/pharmacist-support"
                    element={<PharmacistSupportPage />}
                  />
                  <Route
                    path="/articles-outlet"
                    element={<ArticlesOutletPage />}
                  />
                  <Route
                    path="/articles-visibility"
                    element={<ArticlesVisibilityPage />}
                  />
                  <Route
                    path="/web-commercials"
                    element={<CommercialsPage web />}
                  />
                  <Route
                    path="/mobile-commercials"
                    element={<CommercialsPage mobile />}
                  />
                  <Route
                    path="/rfzo-reminders"
                    element={<UnifiedReminderPage />}
                  />

                  <Route
                    path="/rfzo-patient-reminder"
                    element={
                      <Navigate to="/rfzo-reminders?view=patients" replace />
                    }
                  />
                  <Route
                    path="/rfzo-article-reminder"
                    element={
                      <Navigate to="/rfzo-reminders?view=aggregated" replace />
                    }
                  />

                  <Route path="/patients" element={<OurPatientsPage />} />
                  <Route path="/statistics" element={<Statistics />} />
                  <Route
                    path="/transactions"
                    element={
                      canViewTransactions ? (
                        <TransactionsPage />
                      ) : (
                        <Navigate to={getDefaultRoute()} replace />
                      )
                    }
                  />
                  <Route
                    path="/push-notifications"
                    element={<PushNotificationsPage />}
                  />

                  {/* ITAM-2622 (decision 9): the dual-role pharmacist keeps the
                      whole retail UI and manages the adviser profile from it. */}
                  <Route
                    path="/pharmacist-profile"
                    element={
                      hasPharmacistCapability ? (
                        <PharmacistProfilePage />
                      ) : (
                        <Navigate to={getDefaultRoute()} replace />
                      )
                    }
                  />

                  <Route
                    path="*"
                    element={<Navigate to={getDefaultRoute()} replace />}
                  />
                </>
              )}
            </>
          )}
        </Routes>
      </Layout>
    </>
  );
}

export default App;
